import type { ITextInputFieldProps } from '@sixui/ui';
import type { FieldValues, UseControllerProps } from 'react-hook-form';
import { useContext } from 'react';
import { LabeledContext, TextInputField } from '@sixui/ui';
import { useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { getFormErrorText } from '~/helpers/getFormErrorText';

// TODO: should live in a sixui-forms package

export type IFormDateFieldProps<TFieldValues extends FieldValues> =
  UseControllerProps<TFieldValues> &
    Omit<ITextInputFieldProps, 'defaultValue' | 'value' | 'type'> & {
      readOnly?: boolean;
      required?: boolean;
      hasError?: boolean;
      errorText?: string;

      future?: boolean;
      past?: boolean;
    };

const localDateFromUtc = (utc: string): string => {
  const date = new Date(utc);
  const localDate = date.toLocaleString('sv').replace(' ', 'T').slice(0, 16);

  return localDate;
};

const utcFromLocalDate = (localDate: string): string | undefined => {
  const isValid = !isNaN(new Date(localDate).getTime());
  if (!isValid) {
    return undefined;
  }

  // https://stackoverflow.com/a/28149561/7628220
  const isoString = new Date(localDate).toISOString();

  return isoString;
};

/**
 * @deprecated - use `FormField` with `TextInputField` instead
 */
export const FormDateField = <TFieldValues extends FieldValues>(
  props: IFormDateFieldProps<TFieldValues>,
): React.ReactNode => {
  const {
    name,
    shouldUnregister,
    defaultValue,
    control,
    rules: rulesProp,
    disabled: disabledProp,
    readOnly: readOnlyProp,
    required: requiredProp,
    hasError: hasErrorProp,
    errorText: errorTextProp,
    future,
    past,
    ...other
  } = props;
  const { t } = useTranslation();

  const labeledContext = useContext(LabeledContext);
  const readOnly = readOnlyProp || labeledContext?.readOnly;
  const required = requiredProp || labeledContext?.required;
  const disabled = disabledProp || labeledContext?.disabled;

  const rules = {
    ...rulesProp,
    required: rulesProp?.required || required,
    min: rulesProp?.min ?? (future ? new Date().toISOString() : undefined),
    max: rulesProp?.max ?? (past ? new Date().toISOString() : undefined),
  };

  const { field, fieldState } = useController({
    name,
    rules,
    shouldUnregister,
    defaultValue,
    control,
    disabled,
  });

  const { error: validationError } = fieldState;
  const validationErrorText = validationError
    ? (getFormErrorText(validationError, rules, t) ??
      validationError.type === 'min')
      ? (rulesProp?.min ?? !future)
        ? `You must specify a date after ${JSON.stringify(rules?.min)}`
        : 'You must specify a date in the future.'
      : validationError.type === 'max'
        ? (rulesProp?.max ?? !past)
          ? 'You must specify a date in the past.'
          : `You must specify a date before ${JSON.stringify(rules?.max)}`
        : validationError.message
    : undefined;

  const hasError =
    hasErrorProp || labeledContext?.hasError || !!validationError;
  const errorText =
    errorTextProp ?? labeledContext?.errorText ?? validationErrorText;

  return (
    <TextInputField
      max="9999-12-31T00:00"
      {...other}
      {...field}
      hasError={hasError}
      errorText={errorText}
      type="datetime-local"
      value={field.value ? localDateFromUtc(field.value) : ''}
      disabled={disabled}
      readOnly={readOnly}
      onChange={(event) => {
        if (!event.target.validity.valid) {
          return;
        }
        const localDate = utcFromLocalDate(event.target.value);
        field.onChange(localDate ?? '');
      }}
      clearable
    />
  );
};
